import moment from 'moment'
import { useCallback, useContext, useEffect, useReducer, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast } from 'react-toastify'

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from '@material-ui/core'
import AssignmentIcon from '@material-ui/icons/Assignment'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import SearchIcon from '@material-ui/icons/Search'
import ChatIcon from '@material-ui/icons/Chat'
import ClearIcon from '@material-ui/icons/Clear'
import EditIcon from '@material-ui/icons/Edit'
import DeleteIcon from '@material-ui/icons/Delete'
import HeadsetMicIcon from '@material-ui/icons/HeadsetMic'

import Autocomplete from '@material-ui/lab/Autocomplete'

import ConfirmationModal from '../../components/ConfirmationModal'
import MainContainer from '../../components/MainContainer'
import MainHeader from '../../components/MainHeader'
import MainHeaderButtonsWrapper from '../../components/MainHeaderButtonsWrapper'
import QuickTable from '../../components/QuickTable'
import TaskModal from '../../components/TaskModal'
import TaskCommentsModal from '../../components/TaskCommentsModal'
import NewTicketModalWecloud from '../../components/NewTicketModalWecloud'

import { AuthContext } from '../../context/Auth/AuthContext'
import { SocketContext } from '../../context/Socket/SocketContext'
import toastError from '../../errors/toastError'
import useHelps from '../../hooks/useHelps'
import api from '../../services/api'
import { i18n } from '../../translate/i18n'
import { useStyles } from './style'
import EllipsisWithPopover from '../../components/EllipsisWithPopover'

const reducer = (state, action) => {
  if (action.type === 'LOAD_TASKS') {
    const tasks = action.payload
    const newTasks = []

    tasks.forEach(task => {
      const taskIndex = state.findIndex(s => s.id === task.id)
      if (taskIndex !== -1) {
        state[taskIndex] = task
      } else {
        newTasks.push(task)
      }
    })

    return [...state, ...newTasks]
  }

  if (action.type === 'UPDATE_TASKS') {
    const task = action.payload
    const taskIndex = state.findIndex(s => s.id === task.id)

    if (taskIndex !== -1) {
      state[taskIndex] = task
      return [...state]
    } else {
      return [task, ...state]
    }
  }

  if (action.type === 'DELETE_TASK') {
    const taskId = action.payload
    const taskIndex = state.findIndex(s => s.id === taskId)
    if (taskIndex !== -1) {
      state.splice(taskIndex, 1)
    }
    return [...state]
  }

  if (action.type === 'RESET') {
    return []
  }
}

const Tasks = () => {
  const classes = useStyles()
  const { user } = useContext(AuthContext)
  const history = useHistory()

  const [loading, setLoading] = useState(false)
  const [pageNumber, setPageNumber] = useState(1)
  const [hasMore, setHasMore] = useState(false)
  const [selectedTask, setSelectedTask] = useState(null)
  const [deletingTask, setDeletingTask] = useState(null)
  const [confirmModalOpen, setConfirmModalOpen] = useState(false)
  const [searchParam, setSearchParam] = useState('')
  const [tasks, dispatch] = useReducer(reducer, [])
  const [taskModalOpen, setTaskModalOpen] = useState(false)
  const [helps, setHelps] = useState([])

  // Estados para modal de comentários
  const [commentsModalOpen, setCommentsModalOpen] = useState(false)
  const [selectedTaskForComments, setSelectedTaskForComments] = useState(null)

  // Estados para modal de atendimento
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false)
  const [selectedContactForTicket, setSelectedContactForTicket] = useState(null)

  // Filtros
  const [priorityFilter, setPriorityFilter] = useState('')
  const [userFilter, setUserFilter] = useState('')
  const [createdByFilter, setCreatedByFilter] = useState('')
  const [contactFilter, setContactFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [dateFromFilter, setDateFromFilter] = useState('')
  const [dateToFilter, setDateToFilter] = useState('')
  const [createdAtFromFilter, setCreatedAtFromFilter] = useState('')
  const [createdAtToFilter, setCreatedAtToFilter] = useState('')
  const [tagFilter, setTagFilter] = useState([])

  // Dados para dropdowns
  // const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([])

  // Estados para busca de contatos
  const [contactOptions, setContactOptions] = useState([])
  const [contactLoading, setContactLoading] = useState(false)
  const [contactSearchParam, setContactSearchParam] = useState('')
  const [selectedContact, setSelectedContact] = useState(null)

  // Estado para tags
  const [allTags, setAllTags] = useState([])

  const { list: listHelps } = useHelps()

  const fetchTasks = useCallback(async () => {
    try {
      const params = {
        searchParam,
        pageNumber,
        // Filtrar tarefas por usuário: apenas para usuários que não são admin ou super
        ...(user.profile !== 'admin' &&
          !user.super && { userAccessFilter: user.id }),
        ...(priorityFilter && { priority: priorityFilter }),
        ...(userFilter && { userId: userFilter }),
        ...(createdByFilter && { createdById: createdByFilter }),
        ...(contactFilter && { contactId: contactFilter }),
        ...(statusFilter && { status: statusFilter }),
        ...(dateFromFilter && { dateFrom: dateFromFilter }),
        ...(dateToFilter && { dateTo: dateToFilter }),
        ...(createdAtFromFilter && { createdAtFrom: createdAtFromFilter }),
        ...(createdAtToFilter && { createdAtTo: createdAtToFilter }),
        ...(tagFilter.length > 0 && { tagIds: tagFilter.join(',') })
      }

      const { data } = await api.get('/tasks/', { params })
      dispatch({ type: 'LOAD_TASKS', payload: data.tasks })
      setHasMore(data.hasMore)
      setLoading(false)
    } catch (err) {
      toastError(err)
    }
  }, [
    searchParam,
    pageNumber,
    priorityFilter,
    userFilter,
    createdByFilter,
    contactFilter,
    statusFilter,
    dateFromFilter,
    dateToFilter,
    createdAtFromFilter,
    createdAtToFilter,
    tagFilter,
    user.id,
    user.profile,
    user.super
  ])

  const socketManager = useContext(SocketContext)

  useEffect(() => {
    dispatch({ type: 'RESET' })
    setPageNumber(1)
  }, [
    searchParam,
    priorityFilter,
    userFilter,
    createdByFilter,
    contactFilter,
    statusFilter,
    dateFromFilter,
    dateToFilter,
    createdAtFromFilter,
    createdAtToFilter,
    tagFilter
  ])

  // Reset do contato selecionado quando contactFilter é limpo
  useEffect(() => {
    if (!contactFilter && selectedContact) {
      setSelectedContact(null)
      setContactSearchParam('')
      setContactOptions([])
    }
  }, [contactFilter, selectedContact])

  useEffect(() => {
    setLoading(true)
    const delayDebounceFn = setTimeout(() => {
      fetchTasks()
    }, 500)
    return () => clearTimeout(delayDebounceFn)
  }, [searchParam, pageNumber, fetchTasks])

  useEffect(() => {
    const fetchHelps = async () => {
      try {
        const helpsData = await listHelps()
        setHelps(helpsData)
      } catch (err) {
        toastError(err)
      }
    }

    fetchHelps()
  }, [listHelps])

  // Buscar dados para os dropdowns
  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        // Buscar usuários
        const { data: usersData } = await api.get('/users')
        setUsers(usersData.users || [])

        // Buscar tags
        const { data: tagsData } = await api.get('/tags/list')
        setAllTags(tagsData || [])
      } catch (err) {
        toastError(err)
      }
    }

    fetchFilterData()
  }, [])

  // Busca de contatos com debounce
  useEffect(() => {
    if (contactSearchParam.length < 3) {
      setContactLoading(false)
      setContactOptions([])
      return
    }
    setContactLoading(true)
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get('contacts', {
            params: { searchParam: contactSearchParam }
          })
          setContactOptions(data.contacts)
          setContactLoading(false)
        } catch (err) {
          setContactLoading(false)
          toastError(err)
        }
      }

      fetchContacts()
    }, 500)
    return () => clearTimeout(delayDebounceFn)
  }, [contactSearchParam])

  useEffect(() => {
    const socket = socketManager.GetSocket(user.companyId)

    const onTask = data => {
      if (data.action === 'update' || data.action === 'create') {
        dispatch({ type: 'UPDATE_TASKS', payload: data.task })
      }

      if (data.action === 'delete') {
        dispatch({ type: 'DELETE_TASK', payload: +data.taskId })
      }
    }

    socket.on('task', onTask)

    return () => {
      socket.disconnect()
    }
  }, [user, socketManager])

  const handleOpenTaskModal = () => {
    setSelectedTask(null)
    setTaskModalOpen(true)
  }

  const handleCloseTaskModal = () => {
    setSelectedTask(null)
    setTaskModalOpen(false)
  }

  const handleSearch = event => {
    setSearchParam(event.target.value)
  }

  const canEditTask = task => {
    // Verifica se o usuário está no array de userIds
    let isInUserIds = false
    if (task.userIds) {
      try {
        const userIdsArray =
          typeof task.userIds === 'string'
            ? JSON.parse(task.userIds)
            : task.userIds
        isInUserIds = userIdsArray.includes(user.id)
      } catch (e) {
        console.error('Erro ao parsear userIds:', e)
      }
    }

    // Admin, super, o usuário que criou a tarefa, o usuário atribuído ou qualquer usuário no array userIds pode editá-la
    return (
      user.profile === 'admin' ||
      user.super ||
      Number(task.createdByUserId) === Number(user.id) ||
      Number(task.userId) === Number(user.id) ||
      isInUserIds
    )
  }

  const canDeleteTask = task => {
    // Admin, super ou o usuário que criou a tarefa pode excluí-la (usuários atribuídos não podem deletar)
    return (
      user.profile === 'admin' || user.super || task.createdByUserId === user.id
    )
  }

  const handleEditTask = task => {
    // Verifica se o usuário está no array de userIds
    let isInUserIds = false
    if (task.userIds) {
      try {
        const userIdsArray =
          typeof task.userIds === 'string'
            ? JSON.parse(task.userIds)
            : task.userIds
        isInUserIds = userIdsArray.includes(user.id)
      } catch (e) {
        console.error('Erro ao parsear userIds:', e)
      }
    }

    // Verifica se o usuário pode visualizar/editar a tarefa
    const canView =
      user.profile === 'admin' ||
      user.super ||
      task.createdByUserId === user.id ||
      task.userId === user.id ||
      isInUserIds

    if (!canView) {
      toast.error('Você não tem permissão para acessar esta tarefa.')
      return
    }

    setSelectedTask(task)
    setTaskModalOpen(true)
  }

  const handleOpenCommentsModal = task => {
    setSelectedTaskForComments(task)
    setCommentsModalOpen(true)
  }

  const handleCloseCommentsModal = () => {
    setSelectedTaskForComments(null)
    setCommentsModalOpen(false)
  }

  const handleOpenTicketModal = contact => {
    // Criar um objeto de contato com informações para pré-preenchimento
    const contactForTicket = {
      ...contact,
      // Adicionar o ID do usuário atual para pré-seleção como atendente
      preselectedUserId: user.id
    }

    setSelectedContactForTicket(contactForTicket)
    setNewTicketModalOpen(true)
  }

  const handleCloseTicketModal = ticket => {
    setNewTicketModalOpen(false)
    setSelectedContactForTicket(null)

    if (ticket && ticket.uuid) {
      // Redirecionar para o ticket criado na aba atual
      history.push(`/tickets/${ticket.uuid}`)
    }
  }

  const handleDeleteTask = async taskId => {
    const task = tasks.find(t => t.id === taskId)
    if (task && !canDeleteTask(task)) {
      toast.error('Você não tem permissão para excluir esta tarefa.')
      return
    }

    try {
      await api.delete(`/tasks/${taskId}`)
      toast.success(i18n.t('tasks.toasts.deleted'))
    } catch (err) {
      toastError(err)
    }
    setDeletingTask(null)
    setSearchParam('')
    setPageNumber(1)

    dispatch({ type: 'RESET' })
    setPageNumber(1)
    await fetchTasks()
  }

  const handleUpdateTaskStatus = async (taskId, newStatus) => {
    // Encontrar a tarefa para verificar permissões
    const task = tasks.find(t => t.id === taskId)
    if (!task) {
      toast.error('Tarefa não encontrada.')
      return
    }

    // Verificar se o usuário pode alterar o status (já inclui usuários atribuídos)
    if (!canEditTask(task)) {
      toast.error('Você não tem permissão para alterar o status desta tarefa.')
      return
    }

    try {
      const { data } = await api.put(`/tasks/${taskId}`, { status: newStatus })

      // Atualiza a tarefa no estado local
      dispatch({ type: 'UPDATE_TASKS', payload: data })

      toast.success('Status da tarefa atualizado com sucesso!')
    } catch (err) {
      toastError(err)
    }
  }

  const loadMore = () => {
    setPageNumber(prevState => prevState + 1)
  }

  const handleScroll = e => {
    if (!hasMore || loading) return
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore()
    }
  }

  const getPriorityColor = priority => {
    switch (priority) {
      case 'alta':
        return '#f44336'
      case 'media':
        return '#ff9800'
      case 'baixa':
        return '#4caf50'
      default:
        return '#9e9e9e'
    }
  }

  const getPriorityLabel = priority => {
    return i18n.t(`${priority}`)
  }

  const getStatusColor = status => {
    switch (status) {
      case 'pendente':
        return '#ff9800'
      case 'em_andamento':
        return '#2196f3'
      case 'concluida':
        return '#4caf50'
      case 'vencida':
        return '#f44336'
      default:
        return '#9e9e9e'
    }
  }

  const getStatusLabel = status => {
    const labels = {
      pendente: 'Pendente',
      em_andamento: 'Em andamento',
      concluida: 'Concluída',
      vencida: 'Vencida ⚠️'
    }
    return labels[status] || status
  }

  const formatDate = date => {
    if (!date) return '-'
    return moment(date).format('DD/MM/YYYY HH:mm')
  }

  const isOverdue = dueDate => {
    if (!dueDate) return false
    return moment(dueDate).isBefore(moment())
  }

  // Função para verificar e atualizar tarefas vencidas localmente
  const checkAndUpdateOverdueTasks = useCallback(async () => {
    const now = moment()
    let hasUpdates = false

    // Verifica cada tarefa para identificar as vencidas
    const updatedTasks = tasks.map(task => {
      if (
        task.dueDate &&
        moment(task.dueDate).isBefore(now) &&
        task.status !== 'vencida' &&
        task.status !== 'concluida'
      ) {
        // Marca localmente como vencida
        const updatedTask = { ...task, status: 'vencida' }
        hasUpdates = true

        // Atualiza no backend
        api
          .put(`/tasks/${task.id}`, { status: 'vencida' })
          .then(() => {
            // Mostra notificação para o usuário
            toast.warning(
              `A tarefa "${task.title}" venceu e foi marcada automaticamente como vencida.`,
              {
                position: 'top-right',
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true
              }
            )
            console.log(`Task ${task.id} automatically marked as overdue`)
          })
          .catch(err => {
            console.error(`Error updating overdue task ${task.id}:`, err)
            toastError(err)
          })

        return updatedTask
      }
      return task
    })

    // Atualiza o estado local se houve mudanças
    if (hasUpdates) {
      dispatch({ type: 'LOAD_TASKS', payload: updatedTasks })
    }
  }, [tasks])

  // Executa verificação de tarefas vencidas a cada minuto
  useEffect(() => {
    const interval = setInterval(() => {
      checkAndUpdateOverdueTasks()
    }, 60000) // Verifica a cada minuto

    return () => clearInterval(interval)
  }, [checkAndUpdateOverdueTasks])

  const renderContactOptionLabel = option => {
    if (option.number) {
      return `${option.name} - ${option.number}`
    } else {
      return `${option.name}`
    }
  }

  const handleSelectContact = (e, newValue) => {
    if (newValue?.id) {
      setSelectedContact(newValue)
      setContactFilter(newValue.id)
    } else {
      setSelectedContact(null)
      setContactFilter('')
    }
  }

  // Funções para limpar filtros de data
  const clearDateFromFilter = () => setDateFromFilter('')
  const clearDateToFilter = () => setDateToFilter('')
  const clearCreatedAtFromFilter = () => setCreatedAtFromFilter('')
  const clearCreatedAtToFilter = () => setCreatedAtToFilter('')

  // Função para obter tags de uma tarefa
  const getTaskTags = task => {
    if (!task.tagIds || !allTags.length) return []

    try {
      const tagIds = JSON.parse(task.tagIds)
      return allTags.filter(tag => tagIds.includes(tag.id))
    } catch {
      return []
    }
  }

  const columns = [
    {
      field: 'id',
      headerName: 'ID',
      align: 'center',
      width: 80
    },
    {
      field: 'title',
      headerName: 'Título',
      align: 'left',
      width: 200
    },
    {
      field: 'contact',
      headerName: 'Contato',
      align: 'left',
      width: 150
    },
    {
      field: 'user',
      headerName: 'Usuário',
      align: 'left',
      width: 150
    },
    {
      field: 'createdBy',
      headerName: 'Criado por',
      align: 'left',
      width: 150
    },
    {
      field: 'priority',
      headerName: 'Prioridade',
      align: 'center',
      width: 120
    },
    {
      field: 'status',
      headerName: 'Status',
      align: 'center',
      width: 140
    },
    {
      field: 'tags',
      headerName: 'Tags',
      align: 'left',
      width: 180
    },
    {
      field: 'comments',
      headerName: 'Comentários',
      align: 'center',
      width: 120
    },
    {
      field: 'dueDate',
      headerName: 'Data de Vencimento',
      align: 'center',
      width: 140
    },
    {
      field: 'createdAt',
      headerName: 'Data de Criação',
      align: 'center',
      width: 140
    }
  ]

  const customCellRenderer = {
    title: task => (
      <div>
        <Typography variant="body2" style={{ fontWeight: 'bold' }}>
          {task.title}
        </Typography>
        {task.description && (
          <Typography variant="caption" color="textSecondary">
            {task.description.length > 50
              ? `${task.description.substring(0, 50)}...`
              : task.description}
          </Typography>
        )}
      </div>
    ),
    contact: task => (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <div>
          <Typography variant="body2">{task.contact?.name || '-'}</Typography>
          <Typography variant="caption" color="textSecondary">
            {task.contact?.number || '-'}
          </Typography>
        </div>
        {task.contactId && (
          <Button
            size="small"
            variant="outlined"
            color="primary"
            startIcon={<HeadsetMicIcon />}
            onClick={e => {
              e.stopPropagation()
              handleOpenTicketModal(task.contact)
            }}
            style={{
              fontSize: '0.7rem',
              minWidth: 'auto',
              padding: '2px 6px'
            }}
            title="Abrir atendimento com este contato"
          >
            Atender
          </Button>
        )}
      </div>
    ),
    user: task => {
      // Parsear userIds se existir
      let userIds = []
      if (task.userIds) {
        try {
          userIds =
            typeof task.userIds === 'string'
              ? JSON.parse(task.userIds)
              : task.userIds
        } catch (e) {
          console.error('Erro ao parsear userIds:', e)
        }
      }

      // Se não houver userIds, mostrar apenas o usuário principal
      if (!userIds || userIds.length === 0) {
        return <Typography variant="body2">{task.user?.name || '-'}</Typography>
      }

      // Se houver apenas 1 usuário, mostrar sem chip
      if (userIds.length === 1) {
        return <Typography variant="body2">{task.user?.name || '-'}</Typography>
      }

      // Se houver múltiplos usuários, buscar os nomes de todos os usuários
      const taskUsers = userIds
        .map(userId => users.find(u => u.id === userId))
        .filter(u => u)

      // Renderizar todos os usuários como chips
      return (
        <EllipsisWithPopover style={{ maxWidth: '200px' }}>
          {taskUsers.map((user, index) => (
            <Chip
              key={user.id}
              label={user.name}
              size="small"
              style={{
                backgroundColor: index === 0 ? '#2196f3' : '#1976d2',
                color: 'white',
                marginRight: '4px',
                marginBottom: '4px'
              }}
            />
          ))}
        </EllipsisWithPopover>
      )
    },
    createdBy: task => (
      <Typography variant="body2">{task.createdBy?.name || '-'}</Typography>
    ),
    priority: task => (
      <Chip
        label={getPriorityLabel(task.priority)}
        style={{
          backgroundColor: getPriorityColor(task.priority),
          color: 'white',
          fontWeight: 'bold'
        }}
        size="small"
      />
    ),
    status: task => {
      const currentStatus = task.status || 'pendente'
      const isTaskOverdue =
        task.dueDate && isOverdue(task.dueDate) && currentStatus !== 'concluida'
      const displayStatus =
        isTaskOverdue && currentStatus !== 'vencida' ? 'vencida' : currentStatus

      // Verifica se o usuário pode editar a tarefa (já inclui usuários atribuídos)
      const canUserEditStatus = canEditTask(task)

      if (!canUserEditStatus) {
        return (
          <Chip
            label={getStatusLabel(displayStatus)}
            style={{
              backgroundColor: getStatusColor(displayStatus),
              color: 'white',
              fontWeight: 'bold',
              ...(isTaskOverdue && {
                animation: 'pulse 2s infinite',
                boxShadow: '0 0 8px rgba(244, 67, 54, 0.6)'
              })
            }}
            size="small"
          />
        )
      }

      return (
        <FormControl size="small" style={{ minWidth: '130px' }}>
          <Select
            value={currentStatus}
            onChange={e => {
              e.stopPropagation()
              handleUpdateTaskStatus(task.id, e.target.value)
            }}
            variant="outlined"
            style={{
              backgroundColor: getStatusColor(displayStatus),
              color: 'white',
              fontWeight: 'bold',
              ...(isTaskOverdue && {
                animation: 'pulse 2s infinite',
                boxShadow: '0 0 8px rgba(244, 67, 54, 0.6)'
              })
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  maxHeight: 200
                }
              }
            }}
          >
            <MenuItem value="pendente">
              <Chip
                label="Pendente"
                style={{ backgroundColor: '#ff9800', color: 'white' }}
                size="small"
              />
            </MenuItem>
            <MenuItem value="em_andamento">
              <Chip
                label="Em andamento"
                style={{ backgroundColor: '#2196f3', color: 'white' }}
                size="small"
              />
            </MenuItem>
            <MenuItem value="concluida">
              <Chip
                label="Concluída"
                style={{ backgroundColor: '#4caf50', color: 'white' }}
                size="small"
              />
            </MenuItem>
            <MenuItem value="vencida">
              <Chip
                label="Vencida"
                style={{ backgroundColor: '#f44336', color: 'white' }}
                size="small"
              />
            </MenuItem>
          </Select>
        </FormControl>
      )
    },
    dueDate: task => (
      <Typography
        variant="body2"
        style={{
          color: isOverdue(task.dueDate) ? '#f44336' : 'inherit',
          fontWeight: isOverdue(task.dueDate) ? 'bold' : 'normal'
        }}
      >
        {formatDate(task.dueDate)}
        {isOverdue(task.dueDate) && ' ⚠️'}
      </Typography>
    ),
    createdAt: task => (
      <Typography variant="body2">{formatDate(task.createdAt)}</Typography>
    ),
    tags: task => {
      const tags = getTaskTags(task)
      if (tags.length === 0) {
        return (
          <Typography variant="caption" color="textSecondary">
            Sem tags
          </Typography>
        )
      }

      return (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
          {tags.map((tag, index) => (
            <Chip
              key={tag.id}
              label={tag.name}
              size="small"
              style={{
                backgroundColor: tag.color || '#9e9e9e',
                color: '#fff',
                fontSize: '0.75rem',
                height: '20px'
              }}
            />
          ))}
        </div>
      )
    },
    comments: task => (
      <Button
        size="small"
        variant="outlined"
        startIcon={<ChatIcon />}
        onClick={e => {
          e.stopPropagation()
          handleOpenCommentsModal(task)
        }}
        style={{ fontSize: '0.75rem' }}
      >
        Comentários
      </Button>
    )
  }

  const customRowClass = (item, index) => {
    // Verifica se a tarefa está vencida (não concluída e data de vencimento passou)
    if (
      item.dueDate &&
      isOverdue(item.dueDate) &&
      item.status !== 'concluida'
    ) {
      return classes.overdueRow
    }
    return index % 2 === 0 ? classes.tableRowEven : classes.tableRowOdd
  }

  const sortedTasks = [...tasks].sort((a, b) => {
    // Ordenar por vencimento (vencidas primeiro) e depois por data de criação
    const aOverdue = isOverdue(a.dueDate) && a.status !== 'concluida'
    const bOverdue = isOverdue(b.dueDate) && b.status !== 'concluida'

    if (aOverdue && !bOverdue) return -1
    if (!aOverdue && bOverdue) return 1

    return moment(b.createdAt).diff(moment(a.createdAt))
  })

  // Conta tarefas vencidas
  const overdueTasksCount = tasks.filter(
    task =>
      task.dueDate && isOverdue(task.dueDate) && task.status !== 'concluida'
  ).length

  // Função para renderizar ações customizadas baseada no criador da tarefa
  const renderCustomActions = task => {
    const canUserEdit = canEditTask(task)
    const canUserDelete = canDeleteTask(task)

    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          minHeight: '40px',
          alignItems: 'center'
        }}
      >
        {canUserEdit && (
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation()
              handleEditTask(task)
            }}
            style={{ color: '#1976d2', marginRight: '8px' }}
            title="Editar tarefa"
          >
            <EditIcon />
          </IconButton>
        )}
        {canUserDelete && (
          <IconButton
            size="small"
            onClick={e => {
              e.stopPropagation()
              setConfirmModalOpen(true)
              setDeletingTask(task)
            }}
            style={{ color: '#d32f2f' }}
            title="Excluir tarefa"
          >
            <DeleteIcon />
          </IconButton>
        )}
        {!canUserEdit && !canUserDelete && (
          <span style={{ color: '#9e9e9e', fontSize: '12px' }}>-</span>
        )}
      </div>
    )
  }

  const renderHelpSection = () => {
    if (helps.length === 0) return null

    const tasksHelps = helps.filter(h => h.link === 'Tasks')
    if (tasksHelps.length === 0) return null

    return (
      <Box mb={2}>
        <Typography variant="h6" color="primary" gutterBottom>
          📋 Ajuda - Tarefas
        </Typography>
        {tasksHelps.map((help, index) => (
          <Accordion key={index} style={{ marginBottom: '8px' }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle1">{help.title}</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box>
                {help.description && (
                  <Typography variant="body2" paragraph>
                    {help.description}
                  </Typography>
                )}
                <Box display="flex" gap={2} mt={1}>
                  {help.video && (
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={() =>
                        window.open(
                          `https://www.youtube.com/watch?v=${help.video}`,
                          '_blank'
                        )
                      }
                      startIcon={<span>📹</span>}
                    >
                      Ver Tutorial
                    </Button>
                  )}
                </Box>
              </Box>
            </AccordionDetails>
          </Accordion>
        ))}
      </Box>
    )
  }

  const renderFilters = () => (
    <Box mb={2}>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={2}>
          <Autocomplete
            options={contactOptions}
            loading={contactLoading}
            clearOnBlur
            autoHighlight
            freeSolo
            clearOnEscape
            getOptionLabel={renderContactOptionLabel}
            renderOption={renderContactOptionLabel}
            value={selectedContact}
            onChange={handleSelectContact}
            renderInput={params => (
              <TextField
                {...params}
                label="Todos os clientes"
                variant="outlined"
                size="small"
                onChange={e => setContactSearchParam(e.target.value)}
                placeholder="Digite o nome ou telefone do contato..."
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {contactLoading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </>
                  )
                }}
              />
            )}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl variant="outlined" fullWidth size="small">
            <InputLabel>Todos os status</InputLabel>
            <Select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value)}
              label="Todos os status"
            >
              <MenuItem value="">Todos os status</MenuItem>
              <MenuItem value="pendente">
                <Chip
                  label="Pendente"
                  style={{ backgroundColor: '#ff9800', color: 'white' }}
                  size="small"
                />
              </MenuItem>
              <MenuItem value="em_andamento">
                <Chip
                  label="Em andamento"
                  style={{ backgroundColor: '#2196f3', color: 'white' }}
                  size="small"
                />
              </MenuItem>
              <MenuItem value="concluida">
                <Chip
                  label="Concluída"
                  style={{ backgroundColor: '#4caf50', color: 'white' }}
                  size="small"
                />
              </MenuItem>
              <MenuItem value="vencida">
                <Chip
                  label="Vencida"
                  style={{ backgroundColor: '#f44336', color: 'white' }}
                  size="small"
                />
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl variant="outlined" fullWidth size="small">
            <InputLabel>Todas as prioridades</InputLabel>
            <Select
              value={priorityFilter}
              onChange={e => setPriorityFilter(e.target.value)}
              label="Todas as prioridades"
            >
              <MenuItem value="">Todas as prioridades</MenuItem>
              <MenuItem value="alta">
                <Chip
                  label="Alta"
                  style={{ backgroundColor: '#f44336', color: 'white' }}
                  size="small"
                />
              </MenuItem>
              <MenuItem value="media">
                <Chip
                  label="Média"
                  style={{ backgroundColor: '#ff9800', color: 'white' }}
                  size="small"
                />
              </MenuItem>
              <MenuItem value="baixa">
                <Chip
                  label="Baixa"
                  style={{ backgroundColor: '#4caf50', color: 'white' }}
                  size="small"
                />
              </MenuItem>
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl variant="outlined" fullWidth size="small">
            <InputLabel>Todos os atendentes</InputLabel>
            <Select
              value={userFilter}
              onChange={e => setUserFilter(e.target.value)}
              label="Todos os atendentes"
            >
              <MenuItem value="">Todos os atendentes</MenuItem>
              {users.map(user => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <FormControl variant="outlined" fullWidth size="small">
            <InputLabel>Criado por</InputLabel>
            <Select
              value={createdByFilter}
              onChange={e => setCreatedByFilter(e.target.value)}
              label="Criado por"
            >
              <MenuItem value="">Todos os criadores</MenuItem>
              {users.map(user => (
                <MenuItem key={user.id} value={user.id}>
                  {user.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <TextField
            label="Data Vencimento inicial"
            type="date"
            variant="outlined"
            fullWidth
            size="small"
            value={dateFromFilter}
            onChange={e => setDateFromFilter(e.target.value)}
            InputLabelProps={{
              shrink: true
            }}
            InputProps={{
              endAdornment: dateFromFilter && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={clearDateFromFilter}
                    title="Limpar data"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <TextField
            label="Data Vencimento final"
            type="date"
            variant="outlined"
            fullWidth
            size="small"
            value={dateToFilter}
            onChange={e => setDateToFilter(e.target.value)}
            InputLabelProps={{
              shrink: true
            }}
            InputProps={{
              endAdornment: dateToFilter && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={clearDateToFilter}
                    title="Limpar data"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <TextField
            label="Data Criação inicial"
            type="date"
            variant="outlined"
            fullWidth
            size="small"
            value={createdAtFromFilter}
            onChange={e => setCreatedAtFromFilter(e.target.value)}
            InputLabelProps={{
              shrink: true
            }}
            InputProps={{
              endAdornment: createdAtFromFilter && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={clearCreatedAtFromFilter}
                    title="Limpar data"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={2}>
          <TextField
            label="Data Criação final"
            type="date"
            variant="outlined"
            fullWidth
            size="small"
            value={createdAtToFilter}
            onChange={e => setCreatedAtToFilter(e.target.value)}
            InputLabelProps={{
              shrink: true
            }}
            InputProps={{
              endAdornment: createdAtToFilter && (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    onClick={clearCreatedAtToFilter}
                    title="Limpar data"
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              )
            }}
          />
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Autocomplete
            multiple
            options={allTags}
            getOptionLabel={option => option.name}
            value={allTags.filter(tag => tagFilter.includes(tag.id))}
            onChange={(event, newValue) => {
              setTagFilter(newValue.map(tag => tag.id))
            }}
            renderTags={(value, getTagProps) =>
              value.map((option, index) => (
                <Chip
                  key={option.id}
                  variant="default"
                  label={option.name}
                  size="small"
                  style={{
                    backgroundColor: option.color || '#9e9e9e',
                    color: '#fff',
                    fontSize: '0.75rem',
                    height: '24px'
                  }}
                  {...getTagProps({ index })}
                />
              ))
            }
            renderInput={params => (
              <TextField
                {...params}
                variant="outlined"
                label="Todas as tags"
                placeholder="Selecione as tags"
                size="small"
              />
            )}
            renderOption={option => (
              <Box display="flex" alignItems="center" gap={1}>
                <Chip
                  label={option.name}
                  size="small"
                  style={{
                    backgroundColor: option.color || '#9e9e9e',
                    color: '#fff',
                    fontSize: '0.75rem',
                    height: '20px'
                  }}
                />
              </Box>
            )}
          />
        </Grid>
      </Grid>
    </Box>
  )

  return (
    <MainContainer>
      <ConfirmationModal
        title={
          deletingTask && `${i18n.t('tasks.confirmationModal.deleteTitle')}`
        }
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={() => handleDeleteTask(deletingTask.id)}
      >
        {i18n.t('tasks.confirmationModal.deleteMessage')}
      </ConfirmationModal>

      <TaskModal
        open={taskModalOpen}
        onClose={handleCloseTaskModal}
        reload={fetchTasks}
        taskId={selectedTask && selectedTask.id}
      />

      <TaskCommentsModal
        open={commentsModalOpen}
        onClose={handleCloseCommentsModal}
        task={selectedTaskForComments}
      />

      <NewTicketModalWecloud
        modalOpen={newTicketModalOpen}
        onClose={handleCloseTicketModal}
        initialContact={selectedContactForTicket}
      />

      <MainHeader
        title={`Tarefas ${overdueTasksCount > 0 ? ` (${overdueTasksCount} vencida${overdueTasksCount > 1 ? 's' : ''} ⚠️)` : ''}`}
        description="Gerencie suas tarefas, organize por prioridade e acompanhe os prazos de vencimento."
        showTutorial
      >
        <MainHeaderButtonsWrapper>
          <TextField
            placeholder="Buscar tarefas..."
            variant="outlined"
            type="search"
            value={searchParam}
            onChange={handleSearch}
            className={classes.searchInput}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              )
            }}
          />
          <Button
            variant="contained"
            onClick={handleOpenTaskModal}
            className={classes.buttonNewTask}
            startIcon={<AssignmentIcon />}
          >
            {'Nova Tarefa'}
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      {renderHelpSection()}
      {renderFilters()}

      <Paper className={classes.mainPaper} onScroll={handleScroll}>
        <QuickTable
          data={sortedTasks}
          columns={columns}
          loading={loading}
          customCellRenderer={customCellRenderer}
          customRowClass={customRowClass}
          actions={['edit', 'delete']}
          customActionsRenderer={renderCustomActions}
          emptyMessage="Nenhuma tarefa encontrada."
          containerProps={{ elevation: 0 }}
          showAlternatingRows={true}
          skeletonRows={5}
          onRowClick={task => handleEditTask(task)}
        />
      </Paper>
    </MainContainer>
  )
}

export default Tasks
